import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { PromangRichTextEditor } from "../../utils/PromangRichTextEditor";
import { sanitizeRichHtml } from "../../utils/sanitize-rich-html";
import { trainingApi } from "../../../api/training";
import { normalizeError } from "../../../operationFeedback/normalizeError";
import { findLanguage } from "../../../i18n/languages";
import { buttonClass, inputClass, panelClass, Feedback, guidePayload } from "./trainingAdminShared";
import {
  COURSE_LOCALES,
  mergeMissing,
  missingTranslations,
  readTranslation,
  translatedLocales,
  translationFields,
  writeTranslation,
} from "./trainingTranslations";

const languageName = (code) => findLanguage(code)?.nativeName || code.toUpperCase();

/**
 * Editor step "Språk": the language the course is written in, and text translations
 * for other languages. Structure, answer keys, documents and images are shared.
 */
export default function TrainingTranslationsStep({ draft, patchRevision, disabled }) {
  const { t } = useTranslation();
  const { revision } = draft;
  const base = revision.content_locale || "nb";
  const translations = revision.translations || {};
  const added = translatedLocales(revision);
  const [editing, setEditing] = useState(added[0] || null);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  // The AI answer is merged into the draft as it is when the answer arrives, not as it was when asked.
  const latest = useRef(draft);
  latest.current = draft;
  const current = editing && added.includes(editing) ? editing : null;
  const fields = translationFields(draft);

  const setTranslations = (next) => patchRevision({ translations: next });
  const changeBase = (code) => {
    if (code === base) return;
    // The new course language cannot also be a translation of itself.
    const { [code]: _dropped, ...rest } = translations;
    patchRevision({ content_locale: code, translations: rest });
    if (editing === code) setEditing(null);
  };
  const addLanguage = (code) => {
    setTranslations({ ...translations, [code]: translations[code] || { pages: {}, cards: {} } });
    setEditing(code);
    setMessage(null);
  };
  const removeLanguage = (code) => {
    const { [code]: _removed, ...rest } = translations;
    setTranslations(rest);
    if (editing === code) setEditing(null);
  };
  const updateField = (field, value) =>
    setTranslations({ ...translations, [current]: writeTranslation(translations[current], field, value) });

  const aiTranslate = async (code) => {
    if (!draft.guide.id || aiBusy) return;
    setAiBusy(code);
    setError(null);
    setMessage(null);
    try {
      const payload = guidePayload(draft);
      const result = await trainingApi.translate(draft.guide.id, {
        locale: code,
        guide: { title: payload.guide.title, description: payload.guide.description },
        revision: { pages: payload.revision.pages, content_locale: base },
      });
      const now = latest.current;
      const present = now.revision.translations || {};
      // Removed meanwhile, or it became the course language: nothing to fill.
      if (!present[code] || (now.revision.content_locale || "nb") === code) return;
      const before = missingTranslations(now, present[code]).length;
      const merged = mergeMissing(now, present[code], result.data?.translation);
      setTranslations({ ...present, [code]: merged });
      setEditing(code);
      setMessage(t("trainingAdmin.translation.aiDone", { count: before - missingTranslations(now, merged).length }));
    } catch (e) {
      setError(normalizeError(e));
    } finally {
      setAiBusy(null);
    }
  };

  const visible = current
    ? fields.filter((field) => !onlyMissing || !readTranslation(translations[current], field).trim())
    : [];

  return (
    <div className="space-y-5">
      <section className={`${panelClass} space-y-4`}>
        <div>
          <h3 className="text-lg font-bold text-slate-900">{t("trainingAdmin.translation.baseTitle")}</h3>
          <p className="mt-1 text-sm text-slate-600">{t("trainingAdmin.translation.baseHint")}</p>
        </div>
        <div className="pm-segmented max-w-md" role="group" aria-label={t("trainingAdmin.translation.baseTitle")}>
          {COURSE_LOCALES.map((code) => (
            <button
              key={code}
              type="button"
              lang={code}
              disabled={disabled}
              aria-pressed={code === base}
              className={`pm-segmented__option${code === base ? " is-active" : ""}`}
              onClick={() => changeBase(code)}
            >
              {languageName(code)}
            </button>
          ))}
        </div>
      </section>

      <section className={`${panelClass} space-y-4`}>
        <div>
          <h3 className="text-lg font-bold text-slate-900">{t("trainingAdmin.translation.title")}</h3>
          <p className="mt-1 text-sm text-slate-600">{t("trainingAdmin.translation.hint")}</p>
        </div>
        <Feedback error={error} message={message} />
        {added.length > 0 && (
          <ul className="space-y-2">
            {added.map((code) => {
              const missing = missingTranslations(draft, translations[code]).length;
              return (
                <li key={code} className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3 ${current === code ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white"}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <LanguageIcon className="h-5 w-5 shrink-0 text-[#1463C7]" aria-hidden="true" />
                    <span className="font-semibold text-slate-900" lang={code}>{languageName(code)}</span>
                    <span className={`pm-badge ${missing ? "pm-badge--warn" : "pm-badge--ok"}`}>
                      {missing ? t("trainingAdmin.translation.missing", { count: missing }) : t("trainingAdmin.translation.complete")}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className={buttonClass} disabled={disabled} aria-pressed={current === code} onClick={() => setEditing(code)}>
                      {t("trainingAdmin.translation.edit")}
                    </button>
                    <button
                      type="button"
                      className={buttonClass}
                      disabled={disabled || !draft.guide.id || Boolean(aiBusy) || !missing}
                      aria-busy={aiBusy === code || undefined}
                      title={!draft.guide.id ? t("trainingAdmin.translation.saveFirst") : undefined}
                      onClick={() => aiTranslate(code)}
                    >
                      {aiBusy === code ? <span className="pm-btn__spinner" aria-hidden="true" /> : <SparklesIcon className="h-4 w-4" aria-hidden="true" />}
                      {t(aiBusy === code ? "trainingAdmin.translation.aiBusy" : "trainingAdmin.translation.ai")}
                    </button>
                    <button type="button" className={`${buttonClass} !text-rose-700`} disabled={disabled} onClick={() => removeLanguage(code)}>
                      {t("trainingAdmin.remove")}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">{t("trainingAdmin.translation.add")}</span>
          {COURSE_LOCALES.filter((code) => code !== base && !added.includes(code)).map((code) => (
            <button key={code} type="button" lang={code} className={buttonClass} disabled={disabled} onClick={() => addLanguage(code)}>
              + {languageName(code)}
            </button>
          ))}
        </div>
        {!draft.guide.id && added.length > 0 && <p className="text-xs text-slate-500">{t("trainingAdmin.translation.saveFirst")}</p>}
      </section>

      {current && (
        <section className={`${panelClass} space-y-4`} aria-label={t("trainingAdmin.translation.editorTitle", { language: languageName(current) })}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-bold text-slate-900">
              {t("trainingAdmin.translation.editorTitle", { language: languageName(current) })}
            </h3>
            <label className="flex min-h-[44px] items-center gap-2 text-sm">
              <input type="checkbox" checked={onlyMissing} onChange={(event) => setOnlyMissing(event.target.checked)} />
              {t("trainingAdmin.translation.onlyMissing")}
            </label>
          </div>
          {!visible.length && <p className="text-sm text-slate-600">{t("trainingAdmin.translation.nothingMissing")}</p>}
          <ol className="space-y-4">
            {visible.map((field) => {
              const value = readTranslation(translations[current], field);
              const label = `${field.pageIndex != null ? `${t("trainingAdmin.page")} ${field.pageIndex + 1} · ` : ""}${t(field.label)}${field.kind === "option" ? ` ${field.optionIndex + 1}` : ""}`;
              return (
                <li key={field.id} className="grid gap-3 border-t border-slate-100 pt-4 first:border-t-0 first:pt-0 md:grid-cols-2">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {label} · {languageName(base)}
                    </p>
                    {field.html ? (
                      <div lang={base} className="prose prose-sm max-w-none rounded-xl bg-slate-50 p-3 text-slate-700" dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(field.base) }} />
                    ) : (
                      <p lang={base} className="whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-sm text-slate-700">{field.base}</p>
                    )}
                  </div>
                  <div className="space-y-1" lang={current}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {label} · {languageName(current)}
                    </p>
                    {field.html ? (
                      <PromangRichTextEditor value={value} onChange={(next) => updateField(field, next)} disabled={disabled} templatesEnabled={false} tablesEnabled={false} size="sm" contextMenu={{ insertImage: false }} />
                    ) : field.multiline ? (
                      <textarea className={inputClass} rows={3} value={value} disabled={disabled} aria-label={`${label} · ${languageName(current)}`} onChange={(event) => updateField(field, event.target.value)} />
                    ) : (
                      <input className={inputClass} value={value} disabled={disabled} aria-label={`${label} · ${languageName(current)}`} onChange={(event) => updateField(field, event.target.value)} />
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          <div className="flex justify-end">
            <button type="button" className={buttonClass} onClick={() => setEditing(null)}>
              {t("trainingAdmin.translation.done")}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
